import React, { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import HarbourBrandLockup from './HarbourBrandLockup';
import HarbourIcon from './HarbourIcon';
import HarbourChangePasswordDialog from './HarbourChangePasswordDialog';
import './harbour.css';

/*
 * Harbour/Top bar: brand lockup, centre navigation, user menu. Nav items are
 * prop-driven so tabs only appear once their page exists.
 *
 * The user menu is Harbour/Account menu (Figma 3967:21060, TDI-702): the
 * signed-in email first, as plain text, then Change password for school
 * accounts that sign in with a password, then Sign out. Help, Accessibility,
 * Privacy and Feedback join it once they have somewhere to go (TDI-632,
 * TDI-633).
 */
function initialsOf(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export default function HarbourTopBar({
  navItems = [],
  userName,
  // The address the person is signed in with, shown first in the menu.
  accountEmail,
  // School accounts with a password: adds Change password (needs accountEmail).
  showChangePassword = false,
  onSignOut,
}) {
  const navigate = useNavigate();
  const [changingPassword, setChangingPassword] = useState(false);
  // When Change password opens its dialog, the menu must not hand focus
  // back to its trigger as it closes: that would pull focus out from under
  // the dialog's title.
  const openingDialog = useRef(false);
  // Closing the dialog returns focus to the name button it was opened from.
  const triggerRef = useRef(null);
  const closeDialog = () => {
    setChangingPassword(false);
    if (triggerRef.current) triggerRef.current.focus();
  };
  const canChangePassword = showChangePassword && Boolean(accountEmail);
  return (
    <header className="harbour-top-bar">
      <HarbourBrandLockup />
      <span className="harbour-top-bar__spacer" aria-hidden="true" />
      <nav className="harbour-top-bar__nav" aria-label="Portal">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `harbour-nav-item ${isActive ? 'harbour-nav-item--active' : ''}`.trim()
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button type="button" className="harbour-user-menu" ref={triggerRef}>
            <span className="harbour-monogram" aria-hidden="true">{initialsOf(userName)}</span>
            <span className="truncate max-w-[160px]">{userName}</span>
            <HarbourIcon name="chevron-down" size={20} className="harbour-user-menu__caret" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="harbour harbour-account-menu z-50"
            onCloseAutoFocus={(event) => {
              if (openingDialog.current) {
                event.preventDefault();
                openingDialog.current = false;
              }
            }}
          >
            {accountEmail && (
              <>
                <DropdownMenu.Label className="harbour-account-menu__email">
                  {accountEmail}
                </DropdownMenu.Label>
                <DropdownMenu.Separator className="harbour-account-menu__divider" />
              </>
            )}
            {canChangePassword && (
              <>
                <DropdownMenu.Item
                  className="harbour-account-menu__item"
                  onSelect={() => {
                    openingDialog.current = true;
                    setChangingPassword(true);
                  }}
                >
                  Change password
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="harbour-account-menu__divider" />
              </>
            )}
            <DropdownMenu.Item
              className="harbour-account-menu__item"
              onSelect={() => {
                if (onSignOut) onSignOut();
                else navigate('/rms/sign-in?signedout=1');
              }}
            >
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {changingPassword && (
        <HarbourChangePasswordDialog
          email={accountEmail}
          onClose={closeDialog}
        />
      )}
    </header>
  );
}
